import React, { useState, useEffect } from "react";
import {
  Toolbar,
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Box,
  CircularProgress,
  AppBar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import EditMainCategoryModal from "./MainCategoryModal";
import CategoryModal from "./CategoryModal";
import SubcategoryModal from "./SubCategoryModal";
import AddMainCategoryModal from "./AddMainCategory";
import AddCategory from "./AddCategory";
import AddSubcategory from "./AddSubCategory";
import categoryService from "../../repository/categoryRepository";

const placeholderImage = "https://via.placeholder.com/30";

const ViewCategory = () => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [activeMainCategory, setActiveMainCategory] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMainCategoryModalOpen, setIsMainCategoryModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
  const [isAddMainCategoryModalOpen, setIsAddMainCategoryModalOpen] =
    useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isAddSubcategoryModalOpen, setIsAddSubcategoryModalOpen] =
    useState(false);
  const [currentView, setCurrentView] = useState("main");
  const [loading, setLoading] = useState(true);
  const [currentTitle, setCurrentTitle] = useState("Main Categories");
  const [buttonLabel, setButtonLabel] = useState("Add Main Category");

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fetchMainCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getMainCategories();
      if (!response.success)
        throw new Error(`HTTP error! status: ${response.status}`);
      if (response.data && Array.isArray(response.data)) {
        setMainCategories(response.data);
        setActiveMainCategory(response.data[0]?.mainCategoryId || "");
      }
    } catch (error) {
      console.error("Error fetching main categories:", error);
      setMainCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (mainCategoryId) => {
    setLoading(true);
    try {
      const response = await categoryService.fetchCategories(mainCategoryId);
      if (!response.success)
        throw new Error(`HTTP error! status: ${response.status}`);
      if (response && Array.isArray(response.data)) {
        setCategoriesData(response.data);
        setFilteredCategories(
          response.data.filter(
            (category) => category.mainCategoryId === mainCategoryId,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    setLoading(true);
    try {
      const response = await categoryService.allSubcategory(categoryId);
      if (!response.success)
        throw new Error(`HTTP error! status: ${response.status}`);
      if (response && Array.isArray(response.data)) {
        setSubcategories(response.data);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMainCategories();
  }, []);

  useEffect(() => {
    const titles = {
      main: "Main Categories",
      category: `Categories / ${selectedItem?.mainCategoryName || ""}`,
      subcategory: `Subcategories / ${selectedItem?.categoryName || ""}`,
    };
    setCurrentTitle(titles[currentView]);
    setButtonLabel(
      currentView === "main"
        ? "Add Main Category"
        : currentView === "category"
          ? "Add Category"
          : "Add Subcategory",
    );
  }, [currentView, selectedItem]);

  const handleMainCategoryClick = (mainCategory) => {
    setActiveMainCategory(mainCategory.mainCategoryId);
    setSelectedItem(mainCategory);
    fetchCategories(mainCategory.mainCategoryId);
    setCurrentView("category");
  };

  const handleCategoryClick = (category) => {
    setSelectedItem(category);
    fetchSubcategories(category.categoryId);
    setCurrentView("subcategory");
  };

  const handleBackClick = () => {
    setCurrentView((prevView) =>
      prevView === "subcategory" ? "category" : "main",
    );
  };

  const handleEditClick = (item, mode) => {
    setSelectedItem(item);
    if (mode === "mainCategory") setIsMainCategoryModalOpen(true);
    if (mode === "category") setIsCategoryModalOpen(true);
    if (mode === "subcategory") setIsSubcategoryModalOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);

    await fetchMainCategories();

    if (currentView === "main" && activeMainCategory) {
      await fetchCategories(activeMainCategory);
    } else if (currentView === "category" && selectedItem) {
      await fetchSubcategories(selectedItem.categoryId);
    }

    if (isMainCategoryModalOpen) setIsMainCategoryModalOpen(false);
    if (isCategoryModalOpen) setIsCategoryModalOpen(false);
    if (isSubcategoryModalOpen) setIsSubcategoryModalOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ marginTop: "20px" }}>
      <AppBar position="static" sx={{ backgroundColor: colors.primary[400] }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {currentView !== "main" && (
              <IconButton onClick={handleBackClick}>
                <ArrowBackIcon sx={{ color: colors.grey[100] }} />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ color: colors.grey[100] }}>
              {currentTitle}
            </Typography>
          </div>
          <Button
            color="inherit"
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: "black",
              marginLeft: "auto",
            }}
            onClick={() => {
              if (currentView === "main") {
                setIsAddMainCategoryModalOpen(true);
              } else if (currentView === "category") {
                setIsAddCategoryModalOpen(true);
              } else if (currentView === "subcategory") {
                setIsAddSubcategoryModalOpen(true);
              }
            }}
          >
            {buttonLabel}
          </Button>
        </Toolbar>
      </AppBar>

      <Box mt={2}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="80vh"
          >
            <CircularProgress size={60} sx={{ color: colors.grey[100] }} />
          </Box>
        ) : (
          <>
            {currentView === "main" && (
              <TableContainer
                component={Paper}
                sx={{ backgroundColor: colors.primary[400], maxHeight: 400 }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Image
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "30%",
                          cursor: "pointer",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Main Category
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "20%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mainCategories.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: "center", color: colors.grey[300] }}
                        >
                          No Main Categories Available
                        </TableCell>
                      </TableRow>
                    ) : (
                      mainCategories.map((mainCategory) => (
                        <TableRow key={mainCategory.mainCategoryId}>
                          <TableCell sx={{ padding: "16px" }}>
                            <img
                              src={
                                mainCategory.mainCategoryIcon ||
                                placeholderImage
                              }
                              alt={mainCategory.mainCategoryName}
                              style={{ width: 50, height: 50 }}
                            />
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              handleMainCategoryClick(mainCategory)
                            }
                            sx={{
                              cursor: "pointer",
                              color: colors.grey[100],
                              padding: "16px",
                            }}
                          >
                            {mainCategory.mainCategoryName}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            {mainCategory.description}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            <IconButton
                              onClick={() =>
                                handleEditClick(mainCategory, "mainCategory")
                              }
                            >
                              <EditIcon
                                sx={{ color: colors.greenAccent[500] }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Categories Table */}
            {currentView === "category" && (
              <TableContainer
                component={Paper}
                sx={{ backgroundColor: colors.primary[400], maxHeight: 400 }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Image
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "30%",
                          cursor: "pointer",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "20%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: "center", color: colors.grey[300] }}
                        >
                          No Categories Available
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCategories.map((category) => (
                        <TableRow key={category.categoryId}>
                          <TableCell sx={{ padding: "16px" }}>
                            <img
                              src={category.categoryIcon || placeholderImage}
                              alt={category.categoryName}
                              style={{ width: 50, height: 50 }}
                            />
                          </TableCell>
                          <TableCell
                            onClick={() => handleCategoryClick(category)}
                            sx={{
                              cursor: "pointer",
                              color: colors.grey[100],
                              padding: "16px",
                            }}
                          >
                            {category.categoryName}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            {category.description}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            <IconButton
                              onClick={() =>
                                handleEditClick(category, "category")
                              }
                            >
                              <EditIcon
                                sx={{ color: colors.greenAccent[500] }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Subcategories Table */}
            {currentView === "subcategory" && (
              <TableContainer
                component={Paper}
                sx={{ backgroundColor: colors.primary[400], maxHeight: 400 }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Image
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "30%",
                          cursor: "pointer",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Subcategory
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "30%",
                          cursor: "pointer",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Size
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "25%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "20%",
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          padding: "16px",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subcategories.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: "center", color: colors.grey[300] }}
                        >
                          No Subcategories Available
                        </TableCell>
                      </TableRow>
                    ) : (
                      subcategories.map((subcategory) => (
                        <TableRow key={subcategory.subCategoryId}>
                          <TableCell sx={{ padding: "16px" }}>
                            <img
                              src={
                                subcategory.subcategoryIcon || placeholderImage
                              }
                              alt={subcategory.subCategoryName}
                              style={{ width: 50, height: 50 }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              cursor: "pointer",
                              color: colors.grey[100],
                              padding: "16px",
                            }}
                          >
                            {subcategory.subCategoryName}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            {subcategory.sizeType || "NA"}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            {subcategory.description}
                          </TableCell>
                          <TableCell sx={{ padding: "16px" }}>
                            <IconButton
                              onClick={() =>
                                handleEditClick(subcategory, "subcategory")
                              }
                            >
                              <EditIcon
                                sx={{ color: colors.greenAccent[500] }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      {/* Modals */}
      <EditMainCategoryModal
        open={isMainCategoryModalOpen}
        initialValues={{
          name: selectedItem?.mainCategoryName || "",
          description: selectedItem?.description || "",
          mainCategoryId: selectedItem?.mainCategoryId || "",
          icon: selectedItem?.mainCategoryIcon || "",
        }}
        onClose={() => {
          setIsMainCategoryModalOpen(false);
          fetchMainCategories();
        }}
        onSave={handleSave}
      />

      <CategoryModal
        open={isCategoryModalOpen}
        initialValues={{
          categoryName: selectedItem?.categoryName || "",
          description: selectedItem?.description || "",
          categoryId: selectedItem?.categoryId || "",
          icon: selectedItem?.categoryIcon || "",
          mainCategoryId: activeMainCategory,
        }}
        onClose={() => {
          setIsCategoryModalOpen(false);
          fetchCategories(activeMainCategory);
        }}
        onSave={handleSave}
      />

      <SubcategoryModal
        open={isSubcategoryModalOpen}
        initialValues={{
          subcategoryName: selectedItem?.subCategoryName || "",
          subcategoryId: selectedItem?.subCategoryId || "",
          sizeType: selectedItem?.sizeType || "",
          description: selectedItem?.description || "",
          icon: selectedItem?.subcategoryIcon || "",
        }}
        onClose={() => {
          setIsSubcategoryModalOpen(false);
          fetchSubcategories(selectedItem?.categoryId || "");
        }}
        onSave={handleSave}
      />

      <AddMainCategoryModal
        open={isAddMainCategoryModalOpen}
        onClose={() => setIsAddMainCategoryModalOpen(false)}
        onSave={handleSave}
      />

      <AddCategory
        open={isAddCategoryModalOpen}
        mainCategoryId={activeMainCategory}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onSave={handleSave}
      />

      <AddSubcategory
        open={isAddSubcategoryModalOpen}
        mainCategoryId={activeMainCategory}
        categoryId={selectedItem?.categoryId}
        onClose={() => setIsAddSubcategoryModalOpen(false)}
        onSave={handleSave}
      />
    </Container>
  );
};

export default ViewCategory;
